import * as React from 'react'
import { TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS } from '@atbash-labs/ontology-core'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '../../../components/ui/dialog'
import { Label } from '../../../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group'
import { Textarea } from '../../../components/ui/textarea'
import FileDropZone from '../../IngestPage/shared/FileDropZone'
import {
  EDITOR_DIALOG_CLASS,
  EDITOR_LABEL_CLASS,
  EditorDialogHeader,
  EditorError
} from './editorChrome'

/** The service's cap on a build reason's note. */
export const NOTE_MAX_CHARS = 2000

/** The most example files a config keeps, and so the most one update uploads. */
export const MAX_EXAMPLE_FILES = 5

export interface InstructionSubmit {
  note: string
  files: File[]
  /** Whether the files join the stored examples or take their place. */
  exampleStrategy: 'append' | 'replace'
}

/**
 * *Edit with agent*: a free-text request to the builder
 * (`reason.kind = instruction`). It changes whatever the note asks for —
 * scripts, tables, checks — over the stored examples, plus or instead of up
 * to five new files, and proposes a draft.
 */
export function InstructionDialog({
  open,
  onOpenChange,
  parserKind = 'script',
  version,
  storedExamples = [],
  loading,
  error,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  parserKind?: 'script' | 'agent'
  /** The version the builder starts from. */
  version?: number
  /** The names of the version's stored example files. */
  storedExamples?: readonly string[]
  loading: boolean
  error?: string
  onSubmit: (input: InstructionSubmit) => void
}) {
  const [note, setNote] = React.useState('')
  const [files, setFiles] = React.useState<File[]>([])
  const [strategy, setStrategy] = React.useState<'append' | 'replace'>('append')
  React.useEffect(() => {
    if (open) {
      setNote('')
      setFiles([])
      setStrategy('append')
    }
  }, [open])
  // A new file named like a stored one takes its place, as it does in the service.
  const kept =
    strategy === 'append' || !files.length
      ? storedExamples.filter((name) => !files.some((file) => file.name === name)).length
      : 0
  const total = kept + files.length
  const tooMany = files.length > MAX_EXAMPLE_FILES
  const overCap = !tooMany && total > MAX_EXAMPLE_FILES
  const from = version === undefined ? 'this version' : `v${version}`
  const stored = `${storedExamples.length} stored example file${storedExamples.length === 1 ? '' : 's'}`
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={EDITOR_DIALOG_CLASS}>
        <EditorDialogHeader title="Edit with agent">
          {parserKind === 'agent'
            ? `Say what should change. The builder starts from ${from}, applies your note, reads the example pages again with the result, and parks a draft for review. Add files when the change needs new examples, such as a new form layout.`
            : `Say what should change — the script, the tables or the checks. The builder starts from ${from}, makes the change over the example files, and proposes a draft for review. Add files when the change needs new examples.`}{' '}
          Nothing changes until you save that draft.
        </EditorDialogHeader>
        <label className="flex flex-col gap-1">
          <span className={EDITOR_LABEL_CLASS}>What should change?</span>
          <Textarea
            aria-label="Instruction"
            className="min-h-24 text-xs md:text-xs"
            placeholder={
              parserKind === 'agent'
                ? 'e.g. the form gained a signature box; read it into a signed_by column'
                : 'e.g. read the shift date from the sheet title, or add a shift column'
            }
            maxLength={NOTE_MAX_CHARS}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <FileDropZone
          accept={TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS.join(',')}
          label={`Optional: up to ${MAX_EXAMPLE_FILES} new example files`}
          helperText={`Accepts ${TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS.join(', ')}`}
          onFilesSelected={(list) => setFiles(Array.from(list))}
          onFilesCleared={() => setFiles([])}
        />
        {files.length ? (
          <RadioGroup
            value={strategy}
            onValueChange={(value) => setStrategy(value === 'replace' ? 'replace' : 'append')}
            className="gap-1.5"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="append" id="instruction-examples-append" />
              <Label htmlFor="instruction-examples-append" className="text-xs font-normal">
                Add to the {stored}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="replace" id="instruction-examples-replace" />
              <Label htmlFor="instruction-examples-replace" className="text-xs font-normal">
                Replace them
              </Label>
            </div>
          </RadioGroup>
        ) : null}
        {tooMany ? (
          <p role="alert" className="text-xs text-st-down">
            At most {MAX_EXAMPLE_FILES} files per update.
          </p>
        ) : null}
        {overCap ? (
          <p role="alert" className="text-xs text-st-down">
            That makes {total} example files, and a config keeps at most {MAX_EXAMPLE_FILES}. Choose{' '}
            <em>Replace them</em>, or pick fewer files.
          </p>
        ) : null}
        <EditorError error={error} />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={loading || tooMany || overCap || !note.trim()}
            onClick={() =>
              onSubmit({
                note: note.trim(),
                files,
                exampleStrategy: files.length ? strategy : 'append'
              })
            }
          >
            Start update build
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
